<script setup>
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import EditDialog from '@/components/EditDialog.vue'
  import DataTable from '@/components/DataTable.vue'
  import Loading from '@/components/Loading.vue'
  import { ref, computed, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useEgovListStore } from '@/stores/egovListStore'
  import { useEgovAuthStore } from '@/stores/egovAuthStore'

  const router = useRouter()
  const route = useRoute()

  const dialogStore = useDialogStore()
  const egovListStore = useEgovListStore()
  const egovAuthStore = useEgovAuthStore()

  const breadcrumbs = [{ name: 'ダッシュボード', url: 'dashboard' }, { name: 'e-Gov電子申請一覧' }]

  // UI状態（Vue側で管理）
  const searchText = ref('')
  const sortKey = ref('')
  const sortDesc = ref(false)
  const length = ref('')
  const searchSelect = ref('')

  const loading = ref(null)
  const showPopupEdit = ref(false)
  const showPopupWithdraw = ref(false)
  const showPopupOfficialList = ref(false)
  const withdrawName = ref('')
  const withdrawReason = ref('')
  const isEdit = ref(false)

  // computed
  const currentPage = computed(() => egovListStore.currentPage)
  const totalPages = computed(() => egovListStore.totalPages)

  // methods
  onMounted(async () => {
    // ローディング表示
    loading.value.show()
    try {
      // 認証チェック
      if (await egovAuthStore.checkAuthRequired()) {
        loading.value.hide()
        if (await dialogStore.showConfirmDialog('認証', 'e-Gov電子申請情報を取得するには認証が必要です。認証を開始しますか？')) {
          await egovAuthStore.startAuth()
          return
        } else {
          router.back()
          return
        }
      }

      // ストア初期化
      egovListStore.initTableList()

      // データ取得
      await egovListStore.getApplications()
      loading.value.hide()
    } catch (error) {
      await dialogStore.showAlertDialog('エラー', 'データの取得に失敗しました。')
      loading.value.hide()
    }
  })

  // e-Gov通知詳細取得
  const getEGovNotice = async id => {
    // 通知の存在確認とnoticeSubId取得をストア内で行う
    const result = await egovListStore.getFirstEGovNotice(id)

    if (result.success) {
      // 通知取得成功時の処理
      await dialogStore.showMessageDialog('成功', result.message || '通知を取得しました')
    } else {
      // 通知が存在しない場合や取得に失敗した場合
      await dialogStore.showAlertDialog('エラー', result.message || '通知の取得に失敗しました')
    }
  }

  // e-Gov公文書詳細取得
  const getEGovOfficial = async id => {
    // 公文書の存在確認とnoticeSubId取得をストア内で行う
    const result = await egovListStore.getFirstEGovOfficial(id)

    if (result.success) {
      // 公文書取得成功時の処理
      await dialogStore.showMessageDialog('成功', result.message || '公文書を取得しました')
    } else {
      // 公文書が存在しない場合や取得に失敗した場合
      await dialogStore.showAlertDialog('エラー', result.message || '公文書の取得に失敗しました')
    }
  }

  // e-Gov申請取り下げ
  const withDrawEGov = async () => {
    if (await dialogStore.showConfirmDialog('e-Gov申請取り下げ', 'e-Gov申請を取り下げますか？')) {
      // 取り下げ処理を実行
      await withDrawEGovProcess()
    } else {
      withdrawName.value = ''
      withdrawReason.value = ''
    }
  }

  const withDrawEGovProcess = async () => {
    // ローディング表示
    loading.value.show()
    const result = await egovListStore.withdrawApplication(egovListStore.formData.id, withdrawName.value, withdrawReason.value)
    loading.value.hide()

    if (result.success) {
      await dialogStore.showMessageDialog('ディーモ', result.message)
      // フォームを閉じる
      showPopupWithdraw.value = false
      showPopupEdit.value = false
      withdrawName.value = ''
      withdrawReason.value = ''
    } else {
      await dialogStore.showAlertDialog('ディーモ エラー', result.message)
      showPopupWithdraw.value = false
    }
  }

  // 公文書一覧取得
  const getOfficialDocumentList = async () => {
    const result = await egovListStore.getOfficialDocumentList(egovListStore.formData.id)

    if (result.success) {
      // 公文書一覧表示ダイアログを開く
      showPopupOfficialList.value = true
    } else {
      await dialogStore.showAlertDialog('エラー', result.message)
    }
  }

  // 個別公文書ダウンロード
  const downloadSingleOfficialDocument = async noticeSubId => {
    if (await dialogStore.showConfirmDialog('公文書ダウンロード', 'この公文書をダウンロードしますか？')) {
      const result = await egovListStore.downloadSingleOfficialDocument(egovListStore.formData.id, noticeSubId)

      if (result.success) {
        await dialogStore.showMessageDialog('成功', result.message)
        // 公文書一覧を再取得
        await getOfficialDocumentList()
      } else {
        await dialogStore.showAlertDialog('エラー', result.message)
      }
    }
  }

  // 公文書一覧ダイアログを閉じる
  const closeOfficialDocumentList = () => {
    showPopupOfficialList.value = false
    egovListStore.officialDocumentList = []
  }

  // 申請書再作成
  const recreateApplication = () => {
    router.push({
      name: 'egovapplication',
      query: {
        procId: egovListStore.formData.procId,
        agreementId: egovListStore.formData.agreementId || '',
        action: 'recreate',
        egovId: egovListStore.formData.id,
      },
    })
  }

  // 取り下げ依頼
  const onWithdraw = async () => {
    // ストアのメソッドを使用して取り下げ可能かどうかを判定
    if (!egovListStore.canWithdrawApplication) {
      await dialogStore.showAlertDialog('ディーモ', 'この手続きは取り下げ依頼できません。')
      return
    }
    showPopupWithdraw.value = true
  }

  const onWithdrawOk = async () => {
    if (withdrawName.value === '') {
      await dialogStore.showAlertDialog('ディーモ', '取り下げ依頼者名を入力してください。')
      return
    }
    if (withdrawReason.value === '') {
      await dialogStore.showAlertDialog('ディーモ', '取り下げ理由を入力してください。')
      return
    }
    await withDrawEGov()
    showPopupWithdraw.value = false
  }

  const onWithdrawCancel = () => {
    withdrawName.value = ''
    withdrawReason.value = ''
    showPopupWithdraw.value = false
  }

  // 申請情報ダイアログ表示
  const openEGovDetail = async item => {
    await egovListStore.setFormData(item?.id)
    isEdit.value = !!item
    showPopupEdit.value = true
  }

  // ページング
  const onPaging = async forward => {
    egovListStore.updatePaging(forward)
    await egovListStore.getApplications()
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <h2>e-Gov電子申請</h2>
      <div class="filters">
        <div class="select">
          ステータス:
          <select v-model="searchSelect">
            <option value="">-</option>
            <option v-for="item in egovListStore.searchList" :key="item" :value="item">{{ item }}</option>
          </select>
        </div>
        <div class="search-box">
          <span class="material-symbols-outlined">search</span>
          <input type="search" v-model="searchText" placeholder="検索" />
        </div>
        <!-- ページング -->
        <button type="button" class="prev" @click="onPaging(false)" :disabled="!egovListStore.canGoPrev"></button>
        <button type="button" class="next" @click="onPaging(true)" :disabled="!egovListStore.canGoNext"></button>
        <div class="search-result">{{ currentPage }} / {{ totalPages }}</div>
      </div>
    </div>
    <div class="main-body">
      <DataTable :table-list="egovListStore.tableList" :data-list="egovListStore.applications" :search-text="searchText" :sort-key="sortKey" :sort-desc="sortDesc" @select="openEGovDetail" @length="length = $event" :search-key="'status'" :search-select="searchSelect" :search-list="egovListStore.searchList" />
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />

  <!-- 申請詳細ダイアログ -->
  <EditDialog v-model:visible="showPopupEdit" :show-save-button="false" :show-copy-button="false" delete-button-text="取り下げ依頼" copy-button-text="申請書再作成" save-button-text="公文書取得" :show-delete-button="egovListStore.shouldShowWithdrawButton" @save="getOfficialDocumentList" @copy="recreateApplication" @delete="onWithdraw">
    <template v-slot:title>申請情報</template>
    <template v-slot:content>
      <div class="dialog-edit" v-if="egovListStore.formData">
        <div class="notice">公文書：{{ egovListStore.formData.docCount }}件　 通知：{{ egovListStore.formData.noticeCount }}件</div>
        <table>
          <tr>
            <th>到達番号</th>
            <td>{{ egovListStore.formData.arriveId }}</td>
          </tr>
          <tr>
            <th>法人名</th>
            <td>{{ egovListStore.formData.corporationName }}</td>
          </tr>
          <tr>
            <th>申請者氏名</th>
            <td>{{ egovListStore.formData.applicantName }}</td>
          </tr>
          <tr>
            <th>手続名称</th>
            <td>{{ egovListStore.formData.procName }}</td>
          </tr>
          <tr>
            <th>手続ID</th>
            <td>{{ egovListStore.formData.procId }}</td>
          </tr>
          <tr>
            <th>提出先組織</th>
            <td>{{ egovListStore.formData.submissionDestination }}</td>
          </tr>
          <tr>
            <th>ステータス</th>
            <td>{{ egovListStore.formData.status }}</td>
          </tr>
          <tr>
            <th>ステータス確認日時</th>
            <td>{{ egovListStore.formData.statusCheckDate }}</td>
          </tr>
          <tr>
            <th>到達日時</th>
            <td>{{ egovListStore.formData.arriveDate }}</td>
          </tr>
          <!-- <tr>
            <th>申請区分</th>
            <td>{{ egovListStore.formData.applyType }}</td>
          </tr> -->
          <!-- <tr>
            <th>府省名</th>
            <td>{{ egovListStore.formData.ministryName }}</td>
          </tr> -->
          <tr>
            <th>申請ディーモアカウント</th>
            <td>{{ egovListStore.formData.userName }}</td>
          </tr>
          <!-- <tr>
            <th>申請e-Govアカウント</th>
            <td>{{ egovListStore.formData.appliedAccount }}</td>
          </tr>
          <tr>
            <th>申請e-Govアカウントsub</th>
            <td>{{ egovListStore.formData.appliedEgovAccountSub }}</td>
          </tr>
          <tr>
            <th>申請e-Govアカウントemail</th>
            <td>{{ egovListStore.formData.appliedEgovAccountEmail }}</td>
          </tr>
          <tr>
            <th>納付状況</th>
            <td>-</td>
          </tr> -->
          <tr>
            <th>作成日時</th>
            <td>{{ egovListStore.formData.createdAt }}</td>
          </tr>
          <tr>
            <th>更新日時</th>
            <td>{{ egovListStore.formData.updatedAt }}</td>
          </tr>
        </table>
      </div>
    </template>
  </EditDialog>

  <!-- 取り下げ依頼ダイアログ -->
  <EditDialog v-model:visible="showPopupWithdraw" save-button-text="OK" delete-button-text="キャンセル" :show-copy-button="false" @save="onWithdrawOk" @delete="onWithdrawCancel">
    <template v-slot:title>取り下げ依頼</template>
    <template v-slot:content>
      <div class="dialog-edit">
        <dl>
          <dt>取下げ依頼者氏名</dt>
          <dd>
            <input type="text" v-model="withdrawName" />
          </dd>
          <dt>取下げ理由</dt>
          <dd>
            <textarea v-model="withdrawReason"></textarea>
          </dd>
        </dl>
      </div>
    </template>
  </EditDialog>

  <!-- 公文書一覧ダイアログ -->
  <EditDialog v-model:visible="showPopupOfficialList" :show-save-button="false" :show-copy-button="false" delete-button-text="閉じる" @delete="closeOfficialDocumentList">
    <template v-slot:title>公文書一覧</template>
    <template v-slot:content>
      <div class="dialog-edit">
        <table class="official-document-table">
          <thead>
            <tr>
              <th>通知通番</th>
              <th>公文書タイトル</th>
              <th>許可日</th>
              <th>ダウンロード期限</th>
              <th>状態</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="document in egovListStore.officialDocumentList" :key="document.noticeSubId">
              <td>{{ document.noticeSubId }}</td>
              <td>{{ document.title }}</td>
              <td>{{ document.allowedDate }}</td>
              <td>{{ document.downloadExpiredDate }}</td>
              <td>{{ document.status }}</td>
              <td>
                <button type="button" class="btn-download" @click="downloadSingleOfficialDocument(document.noticeSubId)">ダウンロード</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!egovListStore.officialDocumentList?.length" class="no-documents">公文書がありません</div>
      </div>
    </template>
  </EditDialog>

  <Loading ref="loading" />
</template>

<style lang="less" scoped>
  .official-document-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;

    th,
    td {
      border: 1px solid #ddd;
      padding: 8px 12px;
      text-align: left;
    }

    th {
      background-color: #f5f5f5;
      font-weight: bold;
    }

    .btn-download {
      background-color: #007bff;
      color: white;
      border: none;
      padding: 4px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;

      &:hover {
        background-color: #0056b3;
      }

      &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
      }
    }
  }

  .no-documents {
    text-align: center;
    padding: 20px;
    color: #666;
  }
</style>
